import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import type { FC } from 'react';
import React, { useState } from 'react';

import type { TicketFilterChipT } from './ticketsFiltersModel';

type TicketsFilterChipsPropsT = {
  chips: TicketFilterChipT[];
};

export const TicketsFilterChips: FC<TicketsFilterChipsPropsT> = ({ chips }): JSX.Element => {
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'thin',
      }}
    >
      {chips.map((chip, index): JSX.Element => {
        return (
          <Button
            key={chip.key}
            disableElevation
            disableRipple
            disableFocusRipple
            sx={{
              mt: 0,
              borderRadius: '9999px',
              height: 36,
              minWidth: 'min-content',
              px: 2,
              overflow: 'visible',
              boxShadow: 'none',
              transition: 'all 140ms ease',
              backgroundColor: chip.toggled
                ? (theme): string => theme.palette.primary.main
                : 'transparent',
              // Match WarrQueue: untoggled chips use the primary hoverBorder (or
              // action.hover fallback) for their outline instead of divider, so
              // they're visually consistent with WarrQueue's button filters.
              border: (theme): string =>
                chip.toggled
                  ? 'none'
                  : `1px solid ${theme.palette.primary.hoverBorder || theme.palette.action.hover}`,
              color: (theme): string =>
                chip.toggled ? theme.palette.primary.contrastText : theme.palette.text.primary,
              textTransform: 'none',
              '& .MuiTouchRipple-root': { display: 'none' },
              '&.MuiButton-root': {
                padding: chip.toggled ? '9px 17px' : '8px 16px',
              },
              // Match WarrQueue: no hover rule that flips an untoggled chip to
              // look toggled. Toggled chips keep their primary background on
              // hover; untoggled chips stay outlined.
            }}
            size="small"
            variant="contained"
            onClick={chip.onClick}
            onMouseEnter={(): void => setHoveredIndex(index)}
            onMouseLeave={(): void => setHoveredIndex(-1)}
          >
            <Typography
              noWrap
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: 13,
                lineHeight: '20px',
              }}
            >
              {chip.label}
            </Typography>
            {index === hoveredIndex && chip.deletable && chip.onDelete && (
              <Tooltip title={`Remove ${chip.label} Filter`} arrow>
                <Box
                  role="button"
                  aria-label={`Remove ${chip.label} Filter`}
                  tabIndex={0}
                  onClick={(e): void => {
                    e.stopPropagation();
                    chip.onDelete?.();
                  }}
                  onKeyDown={(e): void => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      chip.onDelete?.();
                    }
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: chip.toggled
                      ? (theme): string => theme.palette.primary.contrastText
                      : (theme): string => theme.palette.text.secondary,
                    backgroundColor: (theme): string => theme.palette.action.hover,
                    '&:hover': {
                      background: (theme): string => theme.palette.action.selected,
                      color: chip.toggled
                        ? (theme): string => theme.palette.primary.contrastText
                        : (theme): string => theme.palette.text.secondary,
                    },
                    ml: 1,
                    width: 22,
                    height: 22,
                    borderRadius: '9999px',
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                >
                  <CloseIcon fontSize="small" />
                </Box>
              </Tooltip>
            )}
          </Button>
        );
      })}
    </Box>
  );
};
